// APP-001: Root component with route-based navigation
import { lazy, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { PhaserGame } from '@/shared/game/PhaserGame';
import { MainHubLayout } from '@/phase1';
import { PrepareLayout } from '@/phase1';
import { BattleLayout } from '@/phase3';
import { ContractGame, useContractGameStore } from '@/phase2';
import { useProjectStore } from '@/shared/stores/projectStore';
import type { ClauseOutcome } from '@/shared/types/phase-contracts';
import { MaintenanceLayout } from '@/phase4';
import '@/phase2/styles/contract-theme.css';

const CardDesignPage = lazy(() => import('@/phase2/ui/catalog/CardDesignPage').then(m => ({ default: m.CardDesignPage })));
const FluidTest = lazy(() => import('@/phase2/ui/dev/FluidTest').then(m => ({ default: m.FluidTest })));
const CyberBorderDemo = lazy(() => import('@/shared/ui/CyberBorderDemo').then(m => ({ default: m.CyberBorderDemo })));

/** Embedded contract game rendered inside the hub panel area */
function ContractGameSlot({
  projectId,
  onComplete,
  onExit,
}: {
  projectId: string;
  onComplete: () => void;
  onExit: () => void;
}) {
  const resetGame = useContractGameStore((s) => s.reset);
  const dispatch = useContractGameStore((s) => s.dispatch);
  const completeContract = useProjectStore((s) => s.completeContract);
  const project = useProjectStore((s) => s.projects[projectId]);

  useEffect(() => {
    resetGame();
    dispatch({ type: 'START_GAME' });
  }, [resetGame, dispatch]);

  const handleComplete = useCallback((engineOutput: { tracks_final: { D: number; S: number; L: number; B: number }; contract_tags: string[]; success: boolean; score: number; clauseOutcomes: ClauseOutcome[] }) => {
    const output = {
      tracksFinal: engineOutput.tracks_final,
      contractTags: engineOutput.contract_tags,
      success: engineOutput.success,
      score: engineOutput.score,
      clauseOutcomes: engineOutput.clauseOutcomes,
    };
    completeContract(projectId, output, project?.projectName || `Project-${projectId.slice(0, 6)}`);
    onComplete();
  }, [projectId, project?.projectName, completeContract, onComplete]);

  return (
    <div className="contract-game">
      <ContractGame onComplete={handleComplete} onExit={onExit} />
    </div>
  );
}

/** Embedded battle game rendered inside the hub panel area */
function BattleGameSlot({
  projectId,
  onComplete,
  onExit,
}: {
  projectId: string;
  onComplete: () => void;
  onExit: () => void;
}) {
  return (
    <div className="battle-game">
      <BattleLayout embeddedProjectId={projectId} onComplete={onComplete} onExit={onExit} />
    </div>
  );
}

/** Embedded maintenance game rendered inside the hub panel area */
function MaintenanceGameSlot({
  projectId,
  onComplete,
  onExit,
}: {
  projectId: string;
  onComplete: () => void;
  onExit: () => void;
}) {
  return (
    <div className="maintenance-game">
      <MaintenanceLayout embeddedProjectId={projectId} onComplete={onComplete} onExit={onExit} />
    </div>
  );
}


function App() {
  const location = useLocation();
  const hidePhaserCanvas = location.pathname.startsWith('/dev/');

  const renderContractGame = useCallback(
    (projectId: string, onComplete: () => void, onExit: () => void) => (
      <ContractGameSlot projectId={projectId} onComplete={onComplete} onExit={onExit} />
    ),
    [],
  );

  const renderBattleGame = useCallback(
    (projectId: string, onComplete: () => void, onExit: () => void) => (
      <BattleGameSlot projectId={projectId} onComplete={onComplete} onExit={onExit} />
    ),
    [],
  );

  const renderMaintenanceGame = useCallback(
    (projectId: string, onComplete: () => void, onExit: () => void) => (
      <MaintenanceGameSlot projectId={projectId} onComplete={onComplete} onExit={onExit} />
    ),
    [],
  );


  return (
    <div id="app">
      <div style={{ display: hidePhaserCanvas ? 'none' : 'block' }}>
        <PhaserGame />
      </div>
      <div id="react-ui" style={{ pointerEvents: 'auto' }}>
        <Routes>
          <Route path="/" element={<Navigate to="/hub" replace />} />
          <Route path="/hub" element={<MainHubLayout renderContractGame={renderContractGame} renderBattleGame={renderBattleGame} renderMaintenanceGame={renderMaintenanceGame} />} />
          <Route path="/hub/prepare" element={<PrepareLayout />} />
          <Route path="/project/:projectId/battle" element={<BattleLayout />} />
          <Route path="/project/:projectId/maintenance" element={<MaintenanceLayout />} />
          <Route path="/dev/card-catalog" element={<CardDesignPage />} />
          <Route path="/dev/fluid-test" element={<FluidTest />} />
          <Route path="/dev/cyber-borders" element={<CyberBorderDemo />} />
          <Route path="*" element={<Navigate to="/hub" replace />} />
        </Routes>
      </div>
    </div>
  );
}

export default App;
